import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DIFFICULTY_RUNGS,
  STREAK_MILESTONES,
  type LeaderboardEntry,
  type PublicRound,
  type RevealPayload,
  type UserStats,
} from '../../../shared/types.ts';

const STREAK_MILESTONE_VALUES = new Set(STREAK_MILESTONES.map((m) => m.at));
const SHARE_URL = 'https://www.witladder.com/';

function buildShareText(
  isCorrect: boolean,
  streakAfter: number,
  difficultySlug: string,
  difficultyName: string,
): string {
  if (!isCorrect) return '';
  if (STREAK_MILESTONE_VALUES.has(streakAfter)) {
    return `🔥 Just hit a ${streakAfter}-streak on witladder — live global trivia where skipping resets the streak. Try beating me: ${SHARE_URL}`;
  }
  if (difficultySlug === 'brainmelter') {
    return `🧠 Nailed a ${difficultyName} on witladder (+5). Live trivia, five difficulty rungs, one global ladder. ${SHARE_URL}`;
  }
  if (streakAfter >= 3) {
    return `On a ${streakAfter}-streak on witladder right now. Live global trivia — come play: ${SHARE_URL}`;
  }
  return `Playing witladder — live global trivia, climbing the ladder. ${SHARE_URL}`;
}

async function shareResult(text: string): Promise<void> {
  if (!text) return;
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: 'witladder', text, url: SHARE_URL });
      return;
    } catch {
      // user cancelled or share failed — fall through to twitter intent.
    }
  }
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
}

interface Props {
  round: PublicRound | null;
  reveal: RevealPayload | null;
  participantCount: number;
  answeredCount: number;
  lastSubmittedAnswerId: number | null;
  onSubmit: (answerId: number) => void;
  miniLeaderboard: LeaderboardEntry[];
  yourMini: LeaderboardEntry | null;
  stats: UserStats | null;
  onOpenHowItWorks: () => void;
  onOpenUser: (username: string) => void;
}

const ANSWER_KEY_LABELS = ['1', '2', '3', '4', '5', '6'];

export function QuizView(props: Props): React.ReactElement {
  const { round, reveal, participantCount, answeredCount, lastSubmittedAnswerId, onSubmit, miniLeaderboard, yourMini, stats, onOpenHowItWorks, onOpenUser } = props;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);

  const phase = useMemo<'question' | 'reveal' | 'idle'>(() => {
    if (!round) return 'idle';
    const ends = new Date(round.endsAt).getTime();
    if (round.phase === 'reveal' || now >= ends) return 'reveal';
    return 'question';
  }, [round, now]);

  const remainingMs = useMemo(() => {
    if (!round) return 0;
    if (phase === 'question') return Math.max(0, new Date(round.endsAt).getTime() - now);
    return Math.max(0, new Date(round.revealEndsAt).getTime() - now);
  }, [round, phase, now]);

  const totalDuration = phase === 'question' ? 20_000 : 5_000;
  const fillPct = Math.max(0, Math.min(100, (remainingMs / totalDuration) * 100));

  // Keyboard 1..6 to answer (or re-pick).
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  useEffect(() => {
    if (!round || phase !== 'question') return;
    const handler = (e: KeyboardEvent): void => {
      const idx = ['1', '2', '3', '4', '5', '6'].indexOf(e.key);
      if (idx >= 0 && round.question.answers[idx]) {
        submitRef.current(round.question.answers[idx].id);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [round, phase]);

  if (!round) {
    return (
      <div className="quiz-empty">
        <p>Waiting for the next round…</p>
      </div>
    );
  }

  const showReveal = phase === 'reveal' && reveal && reveal.roundId === round.roundId;
  const totalAnswered = showReveal ? reveal.totalAnswered : answeredCount;

  return (
    <>
    <div className="quiz-grid">
      <section className="card quiz-main">
        <div className="row gap-sm">
          <span className="pill" style={{ background: 'var(--surface-2)' }}>
            {round.question.category.emoji} {round.question.category.name}
          </span>
          <span className={`pill diff diff-${round.question.difficulty.slug}`}>{round.question.difficulty.name}</span>
          <div className="grow" />
          <div className="row gap-sm small muted">
            {stats && stats.currentStreak > 0 && (
              <span className="streak-chip" title={`Current streak: ${stats.currentStreak}`}>
                🔥 {stats.currentStreak}
              </span>
            )}
            <span>👥 {participantCount} live</span>
            <span>· {totalAnswered} answered</span>
          </div>
        </div>

        <div className="prompt">{round.question.prompt}</div>

        <div className="timer-track">
          <div className={`timer-fill ${phase}`} style={{ width: `${fillPct}%` }} />
          <div className="timer-text">
            {phase === 'question' ? 'Time' : 'Reveal'} · {Math.ceil(remainingMs / 1000)}s
          </div>
        </div>

        {phase === 'question' && lastSubmittedAnswerId !== null && (
          <p className="pick-hint small muted">Locked in — tap another option to change before time’s up.</p>
        )}

        <div className="answers">
          {round.question.answers.map((ans, idx) => {
            const isPicked = lastSubmittedAnswerId === ans.id;
            const distCount = showReveal ? reveal.distribution[ans.id] ?? 0 : 0;
            const distPct = showReveal && totalAnswered > 0 ? Math.round((distCount * 1000) / totalAnswered) / 10 : 0;
            const isCorrect = showReveal && reveal.correctAnswerId === ans.id;
            const isWrongPick = showReveal && isPicked && !isCorrect;
            const cls = [
              'answer',
              isPicked ? 'picked' : '',
              showReveal && isCorrect ? 'correct' : '',
              isWrongPick ? 'wrong' : '',
              showReveal && !isCorrect && !isPicked ? 'fade' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={ans.id}
                className={cls}
                disabled={phase !== 'question'}
                onClick={() => onSubmit(ans.id)}
              >
                <span className="answer-key">{ANSWER_KEY_LABELS[idx]}</span>
                <span className="answer-text">{ans.content}</span>
                {showReveal && (
                  <span className="answer-bar" style={{ width: `${distPct}%` }} />
                )}
                {showReveal && (
                  <span className="answer-pct">{distPct}%</span>
                )}
              </button>
            );
          })}
        </div>

        {showReveal && (
          <div className="reveal-msg">
            {reveal.yourResult?.answerId == null ? (
              <p className="rev rev-skip">⏭️ You didn’t answer this one. Streak reset.</p>
            ) : reveal.yourResult.isCorrect ? (
              <div className="rev-row">
                <p className="rev rev-good">
                  🎉 Correct! +{reveal.yourResult.pointsAwarded}
                  {reveal.yourResult.streakBonus > 0 && (
                    <> · streak bonus +{reveal.yourResult.streakBonus} (streak {reveal.yourResult.streakAfter}!)</>
                  )}
                </p>
                <button
                  type="button"
                  className="share-btn"
                  title="Share this result"
                  onClick={() =>
                    void shareResult(
                      buildShareText(
                        true,
                        reveal.yourResult?.streakAfter ?? 0,
                        round.question.difficulty.slug,
                        round.question.difficulty.name,
                      ),
                    )
                  }
                >
                  <span aria-hidden>↗</span>
                  {reveal.yourResult.streakBonus > 0 ? 'Share the streak' : 'Share'}
                </button>
              </div>
            ) : (
              <p className="rev rev-bad">❌ Wrong. -1</p>
            )}
            {reveal.explanation && <p className="rev-explain">{reveal.explanation}</p>}
          </div>
        )}
      </section>

      <aside className="card mini-board">
        <div className="row">
          <h3>Top players</h3>
          <div className="grow" />
          {yourMini && <span className="small muted">#{yourMini.rank}</span>}
        </div>
        <ol className="lb-list">
          {miniLeaderboard.length === 0 && <li className="muted small">No scores yet — be the first.</li>}
          {miniLeaderboard.map((e) => {
            const clickable = !!e.username;
            return (
              <li
                key={e.browserId ?? e.userId}
                className={`${e.isYou ? 'me' : ''} ${clickable ? 'clickable' : ''}`}
                onClick={clickable ? () => onOpenUser(e.username!) : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={
                  clickable
                    ? (ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          onOpenUser(e.username!);
                        }
                      }
                    : undefined
                }
              >
                <span className="lb-rank">#{e.rank}</span>
                <span className="lb-avatar" style={{ background: e.avatarColor }}>{e.displayName.slice(0, 1).toUpperCase()}</span>
                <span className="lb-name">{e.displayName}</span>
                <span className="lb-score">{e.totalScore}</span>
              </li>
            );
          })}
        </ol>
        {yourMini && !miniLeaderboard.some((m) => m.isYou) && (
          <>
            <div className="lb-sep">· · ·</div>
            <div
              className={`lb-row me ${yourMini.username ? 'clickable' : ''}`}
              onClick={yourMini.username ? () => onOpenUser(yourMini.username!) : undefined}
              role={yourMini.username ? 'button' : undefined}
              tabIndex={yourMini.username ? 0 : undefined}
            >
              <span className="lb-rank">#{yourMini.rank}</span>
              <span className="lb-avatar" style={{ background: yourMini.avatarColor }}>{yourMini.displayName.slice(0, 1).toUpperCase()}</span>
              <span className="lb-name">{yourMini.displayName}</span>
              <span className="lb-score">{yourMini.totalScore}</span>
            </div>
          </>
        )}
      </aside>
    </div>
    <RulesCard onOpenHowItWorks={onOpenHowItWorks} bestStreak={stats?.bestStreak ?? 0} currentStreak={stats?.currentStreak ?? 0} />
    </>
  );
}

function RulesCard({
  onOpenHowItWorks,
  bestStreak,
  currentStreak,
}: {
  onOpenHowItWorks: () => void;
  bestStreak: number;
  currentStreak: number;
}): React.ReactElement {
  const totalWeight = DIFFICULTY_RUNGS.reduce((s, d) => s + d.weight, 0);
  return (
    <section className="card rules-card">
      <div className="row">
        <h3 className="rules-title">The rules, in 30 seconds</h3>
        <div className="grow" />
        <button className="ghost small" onClick={onOpenHowItWorks}>Full guide →</button>
      </div>

      <div className="rules-grid">
        <div className="rules-block">
          <h4>Scoring</h4>
          <p className="muted small">Reward scales with the difficulty of the question.</p>
          <div className="rules-diff-row">
            {DIFFICULTY_RUNGS.map((d) => (
              <div key={d.slug} className={`rules-diff-tile diff-${d.slug}`}>
                <div className="rules-diff-name">{d.name}</div>
                <div className="rules-diff-pts">+{d.pointsCorrect}</div>
                <div className="muted small">{Math.round((d.weight * 100) / totalWeight)}% of rounds</div>
              </div>
            ))}
          </div>
          <p className="muted small rules-foot">
            Wrong answer: <strong className="bad">−1</strong> · Skip: <strong>0</strong> ·
            Easier rungs come up more often, hardest ones rarely — so play stays fun.
          </p>
        </div>

        <div className="rules-block">
          <h4>Streaks</h4>
          <p className="muted small">Correct answers in a row. <strong>Wrong or skip resets it</strong> — yes, even a skip.</p>
          <div className="rules-streak-bar">
            {STREAK_MILESTONES.map((m) => (
              <span key={m.at} className="streak-pill">{m.at}<span className="muted">+{m.bonus}</span></span>
            ))}
          </div>
          <p className="muted small rules-foot">
            Bonus pays out once per streak run. You: <strong>🔥 {currentStreak} now</strong> · best ever {bestStreak}.
          </p>
        </div>
      </div>
    </section>
  );
}
